import { createContext, useContext, useMemo, type FC, type PropsWithChildren } from 'react';
import { type UserFeatures } from '@matrix-forge/graphql-client';

/**
 * Context for accessing user feature flags throughout the application.
 * This context is populated by the studio app's UserInfoProvider and made
 * available to UI components without requiring them to import from studio.
 */
export type FeatureFlagsContextType = Partial<UserFeatures> & { isPlg?: boolean };

export const FeatureFlagsContext = createContext<FeatureFlagsContextType>({});

/**
 * Hook to access feature flags.
 * Returns an empty object if used outside of FeatureFlagsProvider.
 *
 * @example
 * const features = useFeatureFlags();
 * const isEnabled = features.gitlabToolsEnabled ?? false;
 */
export const useFeatureFlags = (): FeatureFlagsContextType => {
  return useContext(FeatureFlagsContext);
};

type FeatureFlagsProviderProps = PropsWithChildren<{
  features?: Partial<UserFeatures>;
  isPlg?: boolean;
}>;

/**
 * Provider component that makes feature flags available to child components.
 * Should be rendered near the root of the application, after authentication.
 *
 * @example
 * <FeatureFlagsProvider features={userInfo?.features || {}}>
 *   <App />
 * </FeatureFlagsProvider>
 */
export const FeatureFlagsProvider: FC<FeatureFlagsProviderProps> = ({
  features = {},
  isPlg = false,
  children,
}) => {
  const value = useMemo(() => ({ ...features, isPlg }), [features, isPlg]);
  return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>;
};

FeatureFlagsProvider.displayName = 'FeatureFlagsProvider';
